import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  LinearProgress
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import BuildIcon from "@mui/icons-material/Build";
import SchoolIcon from "@mui/icons-material/School";
import StarsIcon from "@mui/icons-material/Stars";
import ComputerIcon from "@mui/icons-material/Computer";

export const resumeMeta = {
  hasPhoto: false,
  columns: 2,
  colorOptions: [
    { value: '#4b2c4d', label: 'Purple' },
    { value: '#2196f3', label: 'Blue' },
    { value: '#4caf50', label: 'Green' },
    { value: '#ff9800', label: 'Orange' },
    { value: '#f44336', label: 'Red' }
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "Korina",
  lastName: "Villanueva",
  currentPosition: "Computer Science Student",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "123 Anywhere St., Any City",
  website: "",
  profile: "Highly motivated Computer Science student with a strong foundation in software development and data analysis. Experienced in building scalable web applications and implementing machine learning algorithms.",
};

const defaultLeftSectionOrder = [
  'contact',
  'softSkills',
  'languages'
];

const defaultRightSectionOrder = [
  'profile',
  'education',
  'workExperience',
    'websites',
  'softwareSkills',
  'certifications',
  'accomplishments',
  'volunteering',
  'interests',
  'additionalInfo',
  'references'
];

const Resume33 = ({
  color = "#4b2c4d",
  font = "Roboto, sans-serif",
  fontSize = "12px",
  headingSize = "18px",
  fontStyle = "normal",
  sectionSpacing = 10,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 20,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const data = {
    ...defaultData,
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const [sidebarPages, setSidebarPages] = useState([]);
  const mainContentRef = useRef(null);
  const rightContentRef = useRef(null);
  const sidebarContentRef = useRef(null);

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ─── SHARED STYLE HELPERS ───────────────────────────────────────────────────

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    hyphens: "auto",
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles,
  });

  const getSectionHeaderStyle = () => ({
    ...globalFontStyle,
    fontSize: headingSize,
    fontWeight: "bold",
    mb: `${sectionSpacing / 2}px`,
    color: color,
    overflow: "hidden",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  });

  const getContentSpacingStyle = () => ({
    mb: `${sectionSpacing}px`,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    ...globalFontStyle,
  });

  const getParagraphSpacingStyle = () => ({
    mb: `${paragraphSpacing}px`,
    overflow: "hidden",
    ...globalFontStyle,
  });

  const getListItemSpacingStyle = () => ({
    mb: `${paragraphSpacing / 2}px`,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%",
    overflow: "hidden",
    ...globalFontStyle,
  });

  const getDividerStyle = () => ({
    border: "none",
    height: `${lineWeight}px`,
    bgcolor: "rgba(0, 0, 0, 0.12)",
    mb: `${sectionSpacing}px`,
  });

  // ─── DATA LOADING ─────────────────────────────────────────────────────────

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")) || []);
    setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")) || []);
    setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")) || []);
    setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")) || []);
    setSavedReferences(JSON.parse(localStorage.getItem("referencesList")) || []);
    setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")) || []);
    setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")) || []);
    setSavedInterests(JSON.parse(localStorage.getItem("interests")) || []);
    setSavedWebsites(JSON.parse(localStorage.getItem("websites")) || []);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      currentCustomSections.forEach(sectionName => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = JSON.parse(customSectionData);
            const validItems = (parsedData.items || []).filter(item => item && item.trim() !== '');
            if (validItems.length > 0) sectionsData[sectionName] = validItems;
          }
        } catch (error) {
          console.error(`Error loading custom section ${sectionName}:`, error);
        }
      });
    }

    setCustomSections(sectionsData);

    const savedLeft = localStorage.getItem("resume33LeftSectionOrder");
    const savedRight = localStorage.getItem("resume33RightSectionOrder");

    if (savedLeft && savedRight) {
      setLeftSectionOrder(JSON.parse(savedLeft));
      setRightSectionOrder(JSON.parse(savedRight));
    } else {
      const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      setRightSectionOrder([...defaultRightSectionOrder, ...currentCustomIds]);
    }
  }, []);

  useEffect(() => {
    if (leftSectionOrder.length > 0) localStorage.setItem("resume33LeftSectionOrder", JSON.stringify(leftSectionOrder));
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) localStorage.setItem("resume33RightSectionOrder", JSON.stringify(rightSectionOrder));
  }, [rightSectionOrder]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentCustomIds = Object.keys(customSections).map(n => `custom_${n}`);
      const filtered = rightSectionOrder.filter(s => !s.startsWith('custom_'));
      const newOrder = [...filtered, ...currentCustomIds];
      if (JSON.stringify(newOrder) !== JSON.stringify(rightSectionOrder)) {
        setRightSectionOrder(newOrder);
        localStorage.setItem("resume33RightSectionOrder", JSON.stringify(newOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const filtered = rightSectionOrder.filter(s => !s.startsWith('custom_'));
      if (filtered.length !== rightSectionOrder.length) {
        setRightSectionOrder(filtered);
        localStorage.setItem("resume33RightSectionOrder", JSON.stringify(filtered));
      }
    }
  }, [customSections]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ─── DRAG & DROP ──────────────────────────────────────────────────────────

  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    let newLeft = [...leftSectionOrder];
    let newRight = [...rightSectionOrder];
    if (dragData.side === 'left') newLeft.splice(dragData.index, 1);
    else newRight.splice(dragData.index, 1);
    newLeft.splice(targetIndex, 0, dragData.sectionId);
    setLeftSectionOrder(newLeft);
    setRightSectionOrder(newRight);
    localStorage.setItem("resume33LeftSectionOrder", JSON.stringify(newLeft));
    localStorage.setItem("resume33RightSectionOrder", JSON.stringify(newRight));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    let newLeft = [...leftSectionOrder];
    let newRight = [...rightSectionOrder];
    if (dragData.side === 'left') newLeft.splice(dragData.index, 1);
    else newRight.splice(dragData.index, 1);
    newRight.splice(targetIndex, 0, dragData.sectionId);
    setLeftSectionOrder(newLeft);
    setRightSectionOrder(newRight);
    localStorage.setItem("resume33LeftSectionOrder", JSON.stringify(newLeft));
    localStorage.setItem("resume33RightSectionOrder", JSON.stringify(newRight));
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    localStorage.setItem("resume33LeftSectionOrder", JSON.stringify(defaultLeftSectionOrder));
  };

  const resetRightSectionOrder = () => {
    const customIds = Object.keys(customSections).map(n => `custom_${n}`);
    const newOrder = [...defaultRightSectionOrder, ...customIds];
    setRightSectionOrder(newOrder);
    localStorage.setItem("resume33RightSectionOrder", JSON.stringify(newOrder));
  };

  const resetAllSectionOrder = () => {
    resetLeftSectionOrder();
    resetRightSectionOrder();
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  // ─── SECTION RENDERER ─────────────────────────────────────────────────────

  const renderSection = (sectionId, isSidebar = false) => {
    const className = isSidebar ? "sidebar-section" : "resume-section";

    switch (sectionId) {

      case 'contact':
        return (data.phone || data.email || data.city || data.linkedIn) ? (
          <div key="contact" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>CONTACT</Typography>
            <Stack spacing={1.5} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {data.phone && (
                <Box display="flex" alignItems="center" sx={{ overflow: "hidden" }}>
                  <PhoneIcon sx={{ fontSize: 16, color, mr: 1, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), flex: 1, minWidth: 0 }}>{data.phone}</Typography>
                </Box>
              )}
              {data.email && (
                <Box display="flex" alignItems="center" sx={{ overflow: "hidden" }}>
                  <EmailIcon sx={{ fontSize: 16, color, mr: 1, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), flex: 1, minWidth: 0, wordBreak: "break-all" }}>{data.email}</Typography>
                </Box>
              )}
              {data.city && (
                <Box display="flex" alignItems="center" sx={{ overflow: "hidden" }}>
                  <LocationOnIcon sx={{ fontSize: 16, color, mr: 1, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), flex: 1, minWidth: 0 }}>{data.city}</Typography>
                </Box>
              )}
              {data.linkedIn && (
                <Box display="flex" alignItems="center" sx={{ overflow: "hidden" }}>
                  <LinkedInIcon sx={{ fontSize: 16, color, mr: 1, flexShrink: 0 }} />
                  <Typography sx={{ ...getTextStyle(), flex: 1, minWidth: 0, wordBreak: "break-all" }}>{data.linkedIn}</Typography>
                </Box>
              )}
            </Stack>
          </div>
        ) : null;

      // ── SOFT SKILLS (uses savedSkills from localStorage "skills") ──
      case 'softSkills':
        return savedSkills.length > 0 ? (
          <div key="softSkills" className={className} style={getContentSpacingStyle()}>
            <Divider sx={getDividerStyle()} />
            <Typography sx={getSectionHeaderStyle()}>SOFT SKILLS</Typography>
            {savedSkills.map((skill, index) => (
              <Box key={index} sx={{ mb: 2, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={{ ...getTextStyle(), mb: 0.5 }}>{skill.name}</Typography>
                {skill.rating && (
                  <LinearProgress
                    variant="determinate"
                    value={skill.rating }
                    sx={{
                      bgcolor: isSidebar ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                      "& .MuiLinearProgress-bar": { bgcolor: isSidebar ? "white" : color },
                      height: 4,
                      borderRadius: 2,
                      width: "100%",
                    }}
                  />
                )}
              </Box>
            ))}
          </div>
        ) : null;

      // ── SOFTWARE SKILLS (uses softwareSkills from localStorage "softwareSkills") ──
      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <div key="softwareSkills" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>SOFTWARE SKILLS</Typography>
            {softwareSkills.map((skill, index) => (
              <Box key={index} sx={{ mb: 2, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={{ ...getTextStyle(), mb: 0.5 }}>
                  {skill.name} {skill.level ? `— ${skill.level}%` : ""}
                </Typography>
                {skill.level && (
                  <LinearProgress
                    variant="determinate"
                    value={skill.level }
                    sx={{
                      bgcolor: isSidebar ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                      "& .MuiLinearProgress-bar": { bgcolor: isSidebar ? "white" : color },
                      height: 4,
                      borderRadius: 2,
                      width: "100%",
                    }}
                  />
                )}
              </Box>
            ))}
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className={className} style={getContentSpacingStyle()}>
            <Divider sx={getDividerStyle()} />
            <Typography sx={getSectionHeaderStyle()}>LANGUAGES</Typography>
            {savedLanguages.map((lang, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>
                {lang.name}{lang.level ? ` (${lang.level})` : ""}
              </Typography>
            ))}
          </div>
        ) : null;

      case 'profile':
        return (savedSummary || data.profile) ? (
          <div key="profile" className={className} style={getContentSpacingStyle()}>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: `${sectionSpacing}px`, overflow: "hidden" }}>
              <PersonIcon sx={{ color, flexShrink: 0 }} />
              <Typography sx={getSectionHeaderStyle()}>PROFILE</Typography>
            </Box>
            <Typography sx={getTextStyle({ color: "#555", lineHeight: `${lineSpacing}px`, mb: `${paragraphSpacing}px` })}>
              {savedSummary || data.profile}
            </Typography>
          </div>
        ) : null;

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className={className} style={getContentSpacingStyle()}>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: `${sectionSpacing}px`, overflow: "hidden" }}>
              <SchoolIcon sx={{ color, flexShrink: 0 }} />
              <Typography sx={getSectionHeaderStyle()}>EDUCATION</Typography>
            </Box>
            {educationEntries.map((edu) => {
              const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
              const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
              const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
              const location = edu.educationLevel === "school" ? edu.schoolLocation : edu.collegeLocation;
              return (
                <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "4px", overflow: "hidden" }}>
                    <Typography sx={{ ...getTextStyle({ fontWeight: "bold" }), flex: "1 1 auto", minWidth: 0 }}>
                      {degreeName}{fieldOfStudy ? ` - ${fieldOfStudy}` : ""}
                    </Typography>
                    <Typography sx={{ ...globalFontStyle, flex: "0 0 auto", whiteSpace: "nowrap" }}>
                      {formatDate(edu.gradMonth, edu.gradYear)}
                    </Typography>
                  </Box>
                  <Typography sx={getTextStyle({ color: "#666" })}>
                    {institutionName}{location ? ` | ${location}` : ""}
                    {edu.percentage ? ` | ${edu.percentage}%` : ""}
                    {edu.cgpa ? ` | GPA: ${edu.cgpa}` : ""}
                    {edu.grade ? ` | Grade: ${edu.grade}` : ""}
                  </Typography>
                </Box>
              );
            })}
          </div>
        ) : null;

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>WORK EXPERIENCE</Typography>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={getTextStyle({ fontWeight: "bold", color: "#333" })}>{work.jobTitle}</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "baseline", overflow: "hidden" }}>
                  <Typography sx={{ ...getTextStyle({ color: color, fontWeight: "600", mb: 1 }), flex: "1 1 auto", minWidth: 0 }}>
                    {work.employer}
                  </Typography>
                  <Typography sx={{ ...globalFontStyle, color: color, fontWeight: "600", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                    {formatDate(work.startMonth, work.startYear)} –{" "}
                    {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                {work.description && (
                  <Box
                    sx={{
                      width: "100%", maxWidth: "100%", overflow: "hidden",
                      "& strong": { fontWeight: "bold", ...globalFontStyle },
                      "& em": { fontStyle: "italic", ...globalFontStyle },
                      "& u": { textDecoration: "underline", ...globalFontStyle },
                      "& *": { ...globalFontStyle, wordBreak: "break-word", overflowWrap: "break-word" },
                      ...globalFontStyle,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: work.description
                        .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word;">$1</strong>`)
                        .replace(/\*(.*?)\*/g, `<em style="font-family: ${font}; font-size: ${fontSize}; line-height: ${lineSpacing}px; word-break: break-word;">$1</em>`)
                        .replace(/<u>(.*?)<\/u>/g, `<u style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word;">$1</u>`)
                        .replace(/\n/g, "<br/>"),
                    }}
                  />
                )}
              </Box>
            ))}
          </div>
        ) : null;

      case 'projects':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="projects" className={className} style={getContentSpacingStyle()}>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: `${sectionSpacing}px`, overflow: "hidden" }}>
              <BuildIcon sx={{ color, flexShrink: 0 }} />
              <Typography sx={getSectionHeaderStyle()}>PROJECTS</Typography>
            </Box>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={getTextStyle({ fontWeight: "bold" })}>{work.jobTitle}</Typography>
                {work.description && (
                  <Typography sx={getTextStyle({ mb: 0.5 })}>
                    {work.description.replace(/\*\*(.*?)\*\*/g, '$1').replace(/\*(.*?)\*/g, '$1')}
                  </Typography>
                )}
                <Typography sx={getTextStyle({ color, fontWeight: "bold" })}>Tech: {work.employer}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'achievements':
        return savedAccomplishments.length > 0 ? (
          <div key="achievements" className={className} style={getContentSpacingStyle()}>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: `${sectionSpacing}px`, overflow: "hidden" }}>
              <StarsIcon sx={{ color, flexShrink: 0 }} />
              <Typography sx={getSectionHeaderStyle()}>ACHIEVEMENTS</Typography>
            </Box>
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>• {acc}</Typography>
            ))}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>CERTIFICATIONS</Typography>
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={getTextStyle({ fontWeight: "bold" })}>{cert.name}</Typography>
                <Typography sx={getTextStyle()}>{cert.provider}{cert.year ? ` (${cert.year})` : ""}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>ACCOMPLISHMENTS</Typography>
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>• {acc}</Typography>
            ))}
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>VOLUNTEERING</Typography>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={getTextStyle({ fontWeight: "bold" })}>{vol.subtopic}</Typography>
                <Typography sx={getTextStyle({ color: "#666" })}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography sx={getTextStyle()}>{vol.content}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>INTERESTS</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {savedInterests.map((interest, index) => (
                <Typography key={index} sx={getTextStyle({ mr: 2, mb: `${paragraphSpacing / 2}px` })}>
                  • {interest}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>ADDITIONAL INFORMATION</Typography>
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>• {info}</Typography>
            ))}
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>REFERENCES</Typography>
            {savedReferences.map((ref, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>• {ref}</Typography>
            ))}
          </div>
        ) : null;
case 'websites':
        return savedWebsites.length > 0 ? (
          <div key="websites" className={className} style={getContentSpacingStyle()}>
            <Typography sx={getSectionHeaderStyle()}>WEBSITES / PROFILES</Typography>
            {savedWebsites.map((site, index) => {
              const siteName = typeof site === 'string' ? site : (site.name || site.url || site);
              const siteUrl = typeof site === 'string' ? site : (site.url || site);
              return (
                <Typography key={index} sx={getListItemSpacingStyle()}>
                  • <a 
                      href={siteUrl && siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl || siteName}`}
                      style={{ 
                        color: color, 
                        fontFamily: font, 
                        fontStyle: fontStyle,
                        textDecoration: 'none'
                      }} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      {siteName}
                    </a>
                </Typography>
              );
            })}
          </div>
        ) : null;
      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = (customSections[customSectionName] || []).filter(item => item && item.trim() !== '');
          return items.length > 0 ? (
            <div key={sectionId} className={className} style={getContentSpacingStyle()}>
              <Typography sx={getSectionHeaderStyle()}>
                {customSectionName.replace(/_/g, ' ').toUpperCase()}
              </Typography>
              {items.map((item, i) => (
                <Typography key={i} sx={getListItemSpacingStyle()}>• {item}</Typography>
              ))}
            </div>
          ) : null;
        }
        return null;
    }
  };

  const renderSidebarSections = () => leftSectionOrder.map(id => renderSection(id, true)).filter(Boolean);
  const renderRightContentSections = () => rightSectionOrder.map(id => renderSection(id, false)).filter(Boolean);

  const getSectionTitle = (sectionId) => {
    const titles = {
      contact: 'Contact', softSkills: 'Soft Skills', softwareSkills: 'Software Skills',
      languages: 'Languages', profile: 'Profile', education: 'Education',
      projects: 'Projects', achievements: 'Achievements', workExperience: 'Work Experience',
      certifications: 'Certifications', accomplishments: 'Accomplishments',
      volunteering: 'Volunteering', interests: 'Interests',   websites: 'Websites / Profiles',
      additionalInfo: 'Additional Information', references: 'References'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', ...globalFontStyle }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Sidebar' : 'Right Content'} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1, ...globalFontStyle }}>
          ({sections.length} sections)
        </Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index, side)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease',
              ...globalFontStyle,
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1, ...globalFontStyle }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ ...globalFontStyle }}>{index + 1}</Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center', ...globalFontStyle }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1, ...globalFontStyle }}>
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  // ─── PAGINATION ──────────────────────────────────────────────────────────

  const paginateSidebarContent = useCallback(() => {
    if (!sidebarContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sections = Array.from(sidebarContentRef.current.querySelectorAll('.sidebar-section'));
    let newPages = [];
    let current = [];
    let currentH = 0;
    sections.forEach((section, idx) => {
      const h = section.offsetHeight;
      if (currentH + h > availableHeight) {
        if (current.length > 0) newPages.push([...current]);
        current = [section.outerHTML];
        currentH = h;
      } else {
        current.push(section.outerHTML);
        currentH += h;
      }
      if (idx === sections.length - 1 && current.length > 0) newPages.push([...current]);
    });
    if (newPages.length === 0) newPages.push([]);
    setSidebarPages(newPages);
  }, [page.height, topBottomMargin, leftSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, customSections, lineWeight]);

  const paginateMainContent = useCallback(() => {
    if (!rightContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sections = Array.from(rightContentRef.current.querySelectorAll('.resume-section'));
    let newPages = [];
    let current = [];
    let currentH = 0;
    sections.forEach((section, idx) => {
      const h = section.offsetHeight;
      if (currentH + h > availableHeight) {
        if (current.length > 0) newPages.push([...current]);
        current = [section.outerHTML];
        currentH = h;
      } else {
        current.push(section.outerHTML);
        currentH += h;
      }
      if (idx === sections.length - 1 && current.length > 0) newPages.push([...current]);
    });
    if (newPages.length === 0) newPages.push([]);
    setPages(newPages);
  }, [page.height, topBottomMargin, rightSectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, lineWeight]);

  useEffect(() => {
    const id = setTimeout(() => {
      paginateSidebarContent();
      paginateMainContent();
    }, 500);
    return () => clearTimeout(id);
  }, [
    paginateSidebarContent, paginateMainContent,
    educationEntries, savedSkills, savedSummary, workExperiences,
    savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, customSections, leftSectionOrder, rightSectionOrder,
    sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, lineWeight
  ]);

  // ─── FONT INJECTION FOR dangerouslySetInnerHTML ───────────────────────────

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    let processed = htmlContent;
    
    // Apply heading size to heading elements (detected by all-caps text content)
    const headingRegex = /(<(div|p|span)([^>]*class="[^"]*MuiTypography-root[^"]*"[^>]*)>)([A-Z][A-Z\s]+)(<\/\2>)/g;
    
    processed = processed.replace(headingRegex, (match, openTag, tagName, attrs, textContent, closeTag) => {
      const trimmedText = textContent.trim();
      if (trimmedText === trimmedText.toUpperCase() && trimmedText.length > 1) {
        const commonHeadings = ['CONTACT', 'SOFT SKILLS', 'SOFTWARE SKILLS', 'LANGUAGES', 'PROFILE', 
          'EDUCATION', 'WORK EXPERIENCE', 'PROJECTS', 'ACHIEVEMENTS', 'CERTIFICATIONS', 
          'ACCOMPLISHMENTS', 'VOLUNTEERING', 'INTERESTS','WEBSITES', 'ADDITIONAL INFORMATION', 'REFERENCES'];
        if (commonHeadings.some(h => trimmedText.includes(h))) {
          const styleRegex = /style="([^"]*)"/;
          const styleMatch = attrs.match(styleRegex);
          if (styleMatch) {
            const existingStyle = styleMatch[1];
            if (!existingStyle.includes('font-size:')) {
              const newAttrs = attrs.replace(styleRegex, `style="${existingStyle} font-size: ${headingSize};"`);
              return `<${tagName}${newAttrs}>${textContent}${closeTag}`;
            }
            return match;
          } else {
            const newAttrs = `${attrs} style="font-size: ${headingSize};"`;
            return `<${tagName}${newAttrs}>${textContent}${closeTag}`;
          }
        }
      }
      return match;
    });

    // Apply default font styles to all div, p, span elements
    processed = processed.replace(/<(div|p|span)([^>]*)/g, (match, tagName, attrs) => {
      const styleRegex = /style="([^"]*)"/;
      const styleMatch = attrs.match(styleRegex);
      const defaultStyles = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; max-width: 100%; overflow: hidden; box-sizing: border-box;`;
      if (styleMatch) {
        const existingStyle = styleMatch[1];
        const needsFontSize = !existingStyle.includes('font-size:');
        const fontSizeStyle = needsFontSize ? `font-size: ${fontSize};` : '';
        const mergedStyle = `style="${defaultStyles} ${fontSizeStyle} ${existingStyle}"`;
        return `<${tagName}${attrs.replace(styleRegex, mergedStyle)}`;
      } else {
        return `<${tagName}${attrs} style="${defaultStyles} font-size: ${fontSize};"`;
      }
    });

    return processed;
  };

  // ─── HIDDEN MEASUREMENT CONTAINERS ───────────────────────────────────────

  const renderSidebarForMeasurement = () => (
    <Box ref={sidebarContentRef} sx={{
      visibility: 'hidden', position: 'absolute', top: -1000,
      width: '33%', overflow: "hidden", boxSizing: "border-box",
      pr: `${sideMargins}px`, ...globalFontStyle,
    }}>
      {renderSidebarSections()}
    </Box>
  );

  const renderRightContentForMeasurement = () => (
    <Box ref={rightContentRef} sx={{
      visibility: 'hidden', position: 'absolute', top: -1000,
      width: '67%', overflow: "hidden", boxSizing: "border-box",
      pl: `${sideMargins}px`, ...globalFontStyle,
    }}>
      {renderRightContentSections()}
    </Box>
  );

  const totalPages = Math.max(sidebarPages.length, pages.length, 1);

  // ─── ROOT RENDER ──────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", position: "relative", m: exporting ? 0 : 5, ...globalFontStyle }}>

      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' }, ...globalFontStyle }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, ...globalFontStyle }}>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary" sx={{ ...globalFontStyle }}>
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small" sx={{ ...globalFontStyle }}>
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}

      {renderSidebarForMeasurement()}
      {renderRightContentForMeasurement()}

      {/* Resume Pages */}
      <Box id="resume-container" sx={{ ...globalFontStyle }}>
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              fontFamily: font,
              boxShadow: pageIndex === currentPage ? 3 : 0,
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              flexDirection: "column",
              pageBreakAfter: "always",
              marginTop: `${topBottomMargin}px`,
              marginBottom: `${topBottomMargin}px`,
              paddingLeft: `${sideMargins}px`,
              paddingRight: `${sideMargins}px`,
              overflow: "hidden",
              boxSizing: "border-box",
              ...globalFontStyle,
              "@media print": {
                width: page.width, height: "auto", minHeight: page.height,
                boxShadow: "none", m: 0,
                marginTop: `${topBottomMargin}px`, marginBottom: `${topBottomMargin}px`,
                paddingLeft: `${sideMargins}px`, paddingRight: `${sideMargins}px`,
                display: "flex", overflow: "hidden", ...globalFontStyle,
              },
              '@media print': { display: 'flex' },
            }}
          >
            {/* Header – first page only */}
            {pageIndex === 0 && (
              <Box sx={{
                width: "100%",
                bgcolor: color,
                color: "white",
                p: 4, mt: 4, mb: 4,
                textAlign: "center",
                overflow: "hidden",
                boxSizing: "border-box",
                "@media print": { bgcolor: color, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" },
                ...globalFontStyle,
              }}>
                <Typography sx={{
                  fontSize: "2.5rem", fontWeight: "900", letterSpacing: 2, fontFamily: font,
                  wordBreak: "break-word", overflowWrap: "break-word",
                }}>
                  {data.firstName} {data.lastName}
                </Typography>
                <Typography sx={{
                  fontSize: "1.5rem", letterSpacing: 3, opacity: 0.9, fontFamily: font,
                  wordBreak: "break-word", overflowWrap: "break-word",
                }}>
                  {data.currentPosition}
                </Typography>
              </Box>
            )}

            {/* Two-column content */}
            <Box sx={{ display: "flex", flex: 1, overflow: "hidden", boxSizing: "border-box", ...globalFontStyle }}>

              {/* Left Sidebar (33%) */}
              <Box sx={{
                width: "33%",
                minWidth: 0,
                pr: 3,
                borderRight: `${lineWeight}px solid #eee`,
                overflow: "hidden",
                boxSizing: "border-box",
                ...globalFontStyle,
                "@media print": { width: "33%", display: "inline-block", verticalAlign: "top" },
              }}>
                {sidebarPages[pageIndex] ? (
                  <div
                    style={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box", ...globalFontStyle }}
                    dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(sidebarPages[pageIndex].join('')) }}
                  />
                ) : (
                  <Typography sx={getTextStyle()}>Loading sidebar content...</Typography>
                )}
              </Box>

              {/* Right Main Content (67%) */}
              <Box sx={{
                width: "67%",
                minWidth: 0,
                pl: 3,
                overflow: "hidden",
                boxSizing: "border-box",
                ...globalFontStyle,
                "@media print": { width: "67%", display: "inline-block", verticalAlign: "top" },
              }}>
                {pages[pageIndex] ? (
                  <div
                    style={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box", ...globalFontStyle }}
                    dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pages[pageIndex].join('')) }}
                  />
                ) : (
                  <Typography sx={getTextStyle()}>No content for this page</Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" sx={{ ...globalFontStyle }}>
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, ...globalFontStyle }}>
            Drag and drop sections to reorder them within each column or move sections between left and right columns.
            <br /><strong>Any section can be placed on either side!</strong>
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1, width: "50%" }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary" sx={{ ...globalFontStyle }}>Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained" sx={{ ...globalFontStyle }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume33;